import React, { useContext } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { get, noop, flow } from 'lodash';
import { feeSchedulePrograms, TrackerContext } from '@unite-us/client-utils';
import { BaseCard, BaseCardBody } from '@unite-us/ui';
import {
  useFindRecord,
  usePopulate,
} from 'src/api/APIHooks';
import {
  hasInvoicesAccess,
} from 'src/common/utils/FeatureFlags/flags';
import callOrLog from 'common/utils/callOrLog';
import FeatureFlagContainer from 'src/common/utils/FeatureFlags/FeatureFlagContainer';
import { SERVICES_PROVIDED } from 'common/utils/EventTracker/utils/eventConstants';
import { UPDATE_FEE_SCHEDULE_PROVIDED_SERVICE } from 'src/components/Cases/constants';
import { useFindProviderRequired } from 'src/components/Organization/api/hooks/v1/providerHooks';
import {
  submitFeeScheduleProvidedService as submitFSPS,
} from 'actions/Case/Contact/Group';
import sortProvidedServicesByStartsAt from 'src/components/Cases/utils/sortProvidedServicesByStartsAt';
import useInvoiceSpend from 'src/components/ServiceAuthorization/useInvoiceSpend';
import CardData from './CardData';
import CardHeader from './CardHeader';
import ProvidedServiceForm from '../form/ProvidedServiceForm';
import ProvidedServiceFormWrapper from '../form/ProvidedServiceFormWrapper';

const ProvidedServiceCards = ({
  contactId,
  editMode,
  feeScheduleProvidedServices,
  groupId,
  isEditing,
  metafields,
  program,
  feeScheduleProgram,
  selectedProvidedServiceId,
  serviceCase,
  showServicesProvidedInteractions,
  submitFeeScheduleProvidedService,
  hasInvoicesAccess: userHasInvoicesAccess,
}) => {
  usePopulate(
    'fee_schedule',
    'fee_schedule',
    feeScheduleProgram,
    { queryConfig: { placeholderData: undefined, enabled: !!feeScheduleProgram } },
  );
  const feeSchedule = feeScheduleProgram?.fee_schedule;
  const ignoreSocialCareExpiredDates = get(feeSchedule, 'ignore_social_care_expired_dates', false);

  const isBillable = feeScheduleProgram?.billable;
  const paymentType = feeScheduleProgram?.payment_type;
  const isCostType = feeScheduleProgram && feeSchedulePrograms.isCostPaymentType(paymentType);
  const canInvoiceAboveRemainingAuthorizedAmount = feeScheduleProgram?.can_invoice_above_remaining_authorized_amount;
  const unit = feeScheduleProgram?.unit || '';
  const capInformation = feeScheduleProgram?.cap_information || '';

  const eventTracker = useContext(TrackerContext);
  const edit = (providedServiceId) => () => {
    callOrLog(() => eventTracker(SERVICES_PROVIDED.editNote));
    editMode(providedServiceId);
  };

  const { data } = useFindProviderRequired({ providerId: groupId });
  const isNetworkLead = get(data, 'provider_type') === 'network_lead';

  const submit = (providedService) => () => submitFeeScheduleProvidedService({
    providedService,
    id: providedService.id,
    caseId: serviceCase.id,
    contactId,
    groupId,
    programId: program.id,
  });

  const { data: serviceAuthorizationData } = useFindRecord(
    'service_authorization',
    serviceCase?.service_authorization?.id,
    { queryConfig: { placeholderData: undefined, enabled: !!serviceCase?.service_authorization?.id } },
  );
  const serviceAuthorization = serviceAuthorizationData?.data?.data;
  usePopulate('insurance', 'insurance', serviceAuthorization, { queryConfig: { placeholderData: undefined } });
  usePopulate('insurance.plan', 'plan', serviceAuthorization, { queryConfig: { placeholderData: undefined } });

  const serviceAuthorizationInvoiceInformation = useInvoiceSpend(
    get(serviceCase, 'service_authorization.id'),
  );

  return (
    <div className="space-y-5">
      {
        feeScheduleProvidedServices.length !== 0 &&
        sortProvidedServicesByStartsAt(feeScheduleProvidedServices).map((providedService) => (
          isEditing && (selectedProvidedServiceId === providedService.id) ? (
            <ProvidedServiceFormWrapper
              key={providedService.id}
              contactId={contactId}
              serviceCase={serviceCase}
              providedService={providedService}
            >
              <ProvidedServiceForm
                isBillable={isBillable}
                cancelFunction={() => editMode('')}
                contactId={contactId}
                formName={`${UPDATE_FEE_SCHEDULE_PROVIDED_SERVICE}-${providedService.id}`}
                serviceCase={serviceCase}
                selectedProvidedServiceId={selectedProvidedServiceId}
                providedServiceId={providedService.id}
                isCostType={isCostType}
                isEditing={isEditing}
              />
            </ProvidedServiceFormWrapper>
          ) : (
            <BaseCard key={providedService.id} className="fee-schedule-provided-service-card">
              <BaseCardBody>
                <div data-testid={`provided-service-card-${providedService.id}`}>
                  <CardHeader
                    className="py-4 px-8"
                    isBillable={isBillable}
                    isNetworkLead={isNetworkLead}
                    metafields={metafields}
                    onEdit={edit(providedService.id)}
                    onSubmit={submit(providedService)}
                    programName={get(serviceCase, 'program.name')}
                    providedService={providedService}
                    showInteractions={showServicesProvidedInteractions}
                    ignoreSocialCareExpiredDates={ignoreSocialCareExpiredDates}
                    canInvoiceAboveRemainingAuthorizedAmount={canInvoiceAboveRemainingAuthorizedAmount}
                    feeScheduleProgram={feeScheduleProgram}
                    serviceAuthorization={serviceAuthorization}
                    serviceAuthorizationInvoiceInformation={serviceAuthorizationInvoiceInformation}
                    groupId={groupId}
                    personId={get(serviceCase, 'person.id')}
                    hasInvoicesAccess={userHasInvoicesAccess}
                  />
                  <hr className="border-dark-fill-blue" />
                  <CardData
                    capInformation={capInformation}
                    className="py-4 px-8 box-border"
                    paymentType={paymentType}
                    programName={get(serviceCase, 'program.name')}
                    providedService={providedService}
                    unit={unit}
                    metafields={metafields}
                  />
                </div>
              </BaseCardBody>
            </BaseCard>
          )
        ))
      }
    </div>
  );
};

ProvidedServiceCards.propTypes = {
  contactId: PropTypes.string,
  editMode: PropTypes.func,
  feeScheduleProvidedServices: PropTypes.array.isRequired,
  groupId: PropTypes.string,
  isEditing: PropTypes.bool,
  program: PropTypes.object,
  metafields: PropTypes.array,
  serviceCase: PropTypes.shape({
    id: PropTypes.string,
    contact: PropTypes.object,
    service_authorization: PropTypes.object,
  }).isRequired,
  selectedProvidedServiceId: PropTypes.string,
  submitFeeScheduleProvidedService: PropTypes.func.isRequired,
  showServicesProvidedInteractions: PropTypes.bool,
  feeScheduleProgram: PropTypes.object,
  hasInvoicesAccess: PropTypes.bool.isRequired,
};

ProvidedServiceCards.defaultProps = {
  contactId: '',
  editMode: noop,
  groupId: '',
  isEditing: false,
  metafields: [],
  program: '',
  feeScheduleProgram: undefined,
  selectedProvidedServiceId: '',
  showServicesProvidedInteractions: true,
};

const mapStateToProps = (state) => {
  const groupId = state.session.groupId;

  return {
    groupId,
    hasInvoicesAccess: hasInvoicesAccess(state),
  };
};

export default flow(
  FeatureFlagContainer,
  connect(mapStateToProps, {
    submitFeeScheduleProvidedService: submitFSPS,
  }),
)(ProvidedServiceCards);
